import { Container, Box, Typography, makeStyles } from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import StarIcon from "@material-ui/icons/Star";
import CodeIcon from "@material-ui/icons/Code";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import EmojiEventsOutlinedIcon from "@material-ui/icons/EmojiEventsOutlined";
import FlagOutlinedIcon from "@material-ui/icons/FlagOutlined";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";

import { Loading } from "../../components/shared/loading";
import { HeroParticles } from "../../components/shared/HeroParticles";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { Reveal } from "../../components/shared/Reveal";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const JUNK = ["n/a", "na", "-", "none", "null", "nil"];
const cleanSkills = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s && !JUNK.includes(s.toLowerCase()));

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  "@keyframes orbDrift": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(14px, -12px) scale(1.12)" },
  },
  "@keyframes orbDriftAlt": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(-16px, 10px) scale(1.08)" },
  },

  // ── Hero ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "45vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroOrb: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(60px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroOrbOne: {
    width: 340,
    height: 340,
    top: "-16%",
    right: "6%",
    background:
      "radial-gradient(circle, rgba(199,125,199,0.35), transparent 68%)",
    animation: "$orbDrift 9s ease-in-out infinite",
  },
  heroOrbTwo: {
    width: 300,
    height: 300,
    bottom: "-22%",
    left: "2%",
    background:
      "radial-gradient(circle, rgba(155,59,155,0.3), transparent 68%)",
    animation: "$orbDriftAlt 11s ease-in-out infinite",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "44px 20px 70px",
    maxWidth: 700,
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "2.4px",
    textTransform: "uppercase",
    color: "#e0b8e0",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(199,125,199,0.35)",
    borderRadius: 30,
    padding: "6px 16px",
    marginBottom: 18,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) both",
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#c77dc7",
      boxShadow: "0 0 10px #c77dc7",
    },
  },
  heroTitle: {
    fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
    fontWeight: 700,
    marginBottom: 10,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroDivider: {
    width: 90,
    height: 3,
    background:
      "linear-gradient(90deg, transparent, #c77dc7 30%, #e0b8e0 50%, #c77dc7 70%, transparent)",
    margin: "16px auto",
    borderRadius: 2,
    animation: "$heroExpand 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
  },
  heroSubtitle: {
    fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
    fontWeight: 400,
    opacity: 0.9,
    maxWidth: 520,
    margin: "0 auto",
    lineHeight: 1.5,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both",
  },

  // ── Floating stats bar ──
  statsBar: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px 34px",
    width: "fit-content",
    maxWidth: "calc(100% - 32px)",
    margin: "-30px auto 0",
    padding: "14px 28px",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 20,
    boxShadow: "0 18px 44px rgba(26,0,26,0.16)",
  },
  statItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
  },
  statIcon: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    background: "linear-gradient(135deg, #6b1b6b, #9b3b9b)",
    boxShadow: "0 6px 14px rgba(76,0,76,0.22)",
    "& svg": { fontSize: 17 },
  },
  statLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#3a003a",
    whiteSpace: "nowrap",
  },

  // ── Page + sections ──
  pageWrapper: {
    background: "#f5f3f6",
    paddingBottom: 52,
  },
  sectionHead: {
    textAlign: "center",
    margin: "46px auto 26px",
    maxWidth: 560,
  },
  sectionKicker: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "2.4px",
    marginBottom: 8,
  },
  sectionKickerGold: { color: "#b8860b" },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#4c004c",
    letterSpacing: "-0.01em",
    marginBottom: 6,
  },
  sectionSubtitle: {
    fontSize: "0.85rem",
    color: "#666666",
    lineHeight: 1.6,
  },

  // ── Leadership: gold executive slabs ──
  leaderGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "24px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: "18px",
    },
  },
  leaderCard: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "22px",
    overflow: "hidden",
    borderRadius: 20,
    padding: "26px 26px",
    background:
      "linear-gradient(160deg, #1a001a 0%, #2d002d 55%, #3a003a 100%)",
    border: "1px solid rgba(242,193,78,0.16)",
    boxShadow: "0 14px 40px rgba(26,0,26,0.28)",
    textDecoration: "none",
    transition: "all 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:visited": { textDecoration: "none" },
    "&::before": {
      content: '""',
      position: "absolute",
      width: 260,
      height: 260,
      borderRadius: "50%",
      top: -120,
      right: -80,
      background:
        "radial-gradient(circle, rgba(242,193,78,0.16), transparent 70%)",
      filter: "blur(30px)",
      pointerEvents: "none",
      transition: "opacity 0.45s ease",
      opacity: 0.7,
    },
    "&:hover": {
      transform: "translateY(-7px)",
      borderColor: "rgba(242,193,78,0.45)",
      boxShadow: "0 28px 60px rgba(26,0,26,0.45)",
    },
    "&:hover::before": { opacity: 1 },
    "&:hover $leaderArrow": {
      background: "linear-gradient(135deg, #fbeeb5, #f2c14e)",
      color: "#3a2a00",
      transform: "translateX(4px)",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      textAlign: "center",
      padding: "28px 20px 24px",
    },
  },
  leaderPortraitWrap: {
    position: "relative",
    flexShrink: 0,
  },
  leaderPortrait: {
    width: 118,
    height: 118,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "3px solid transparent",
    backgroundImage:
      "linear-gradient(#1a001a, #1a001a), conic-gradient(#f2c14e, #fbeeb5, #b8860b, #f2c14e)",
    backgroundOrigin: "border-box",
    backgroundClip: "content-box, border-box",
    boxShadow: "0 0 0 7px rgba(242,193,78,0.1), 0 16px 36px rgba(0,0,0,0.5)",
  },
  leaderCrest: {
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #fbeeb5, #f2c14e)",
    color: "#3a2a00",
    border: "2px solid #1a001a",
    boxShadow: "0 6px 16px rgba(242,193,78,0.45)",
    "& svg": { fontSize: 16 },
  },
  leaderBody: {
    position: "relative",
    zIndex: 1,
    minWidth: 0,
    flex: 1,
  },
  leaderKicker: {
    fontSize: "0.56rem",
    fontWeight: 700,
    color: "#f2c14e",
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: 6,
  },
  leaderName: {
    fontSize: "1.02rem",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.01em",
    marginBottom: 3,
  },
  leaderDesig: {
    fontSize: "0.74rem",
    fontWeight: 500,
    color: "#e0b8e0",
    marginBottom: 10,
    lineHeight: 1.4,
  },
  leaderDesc: {
    fontSize: "0.76rem",
    color: "rgba(255,255,255,0.62)",
    lineHeight: 1.6,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginBottom: 14,
  },
  leaderFoot: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    [theme.breakpoints.down("xs")]: { justifyContent: "center" },
  },
  leaderArrow: {
    width: 30,
    height: 30,
    borderRadius: 9,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(242,193,78,0.14)",
    border: "1px solid rgba(242,193,78,0.4)",
    color: "#f2c14e",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 16 },
  },
  leaderFootLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "rgba(255,255,255,0.75)",
    textTransform: "uppercase",
    letterSpacing: "1.4px",
  },

  // ── Core developers: purple builder cards ──
  devGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "18px",
    },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  devCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    background: "#ffffff",
    borderRadius: 18,
    padding: "26px 20px 22px",
    border: "1px solid rgba(76,0,76,0.07)",
    boxShadow: "0 8px 26px rgba(76,0,76,0.07)",
    textDecoration: "none",
    overflow: "hidden",
    transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:visited": { textDecoration: "none" },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: "linear-gradient(90deg, #6b1b6b, #c77dc7, #6b1b6b)",
      opacity: 0,
      transition: "opacity 0.4s ease",
    },
    "&:hover": {
      transform: "translateY(-7px)",
      boxShadow: "0 22px 50px rgba(76,0,76,0.16)",
      borderColor: "rgba(155,59,155,0.25)",
    },
    "&:hover::before": { opacity: 1 },
    "&:hover $devArrowRow": { color: "#4c004c" },
    "&:hover $devPortrait": { transform: "scale(1.04)" },
  },
  devPortraitWrap: {
    position: "relative",
    marginBottom: 14,
  },
  devPortrait: {
    width: 96,
    height: 96,
    borderRadius: 24,
    objectFit: "cover",
    display: "block",
    border: "3px solid transparent",
    backgroundImage:
      "linear-gradient(#ffffff, #ffffff), linear-gradient(135deg, #c77dc7, #6b1b6b, #c77dc7)",
    backgroundOrigin: "border-box",
    backgroundClip: "content-box, border-box",
    boxShadow: "0 12px 28px rgba(76,0,76,0.22)",
    transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
  },
  devCrest: {
    position: "absolute",
    bottom: -7,
    right: -7,
    width: 28,
    height: 28,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #9b3b9b, #6b1b6b)",
    color: "#ffffff",
    border: "2px solid #ffffff",
    boxShadow: "0 6px 14px rgba(155,59,155,0.4)",
    "& svg": { fontSize: 14 },
  },
  devName: {
    fontSize: "0.92rem",
    fontWeight: 700,
    color: "#2d002d",
    marginBottom: 3,
  },
  devDesig: {
    fontSize: "0.7rem",
    fontWeight: 500,
    color: "#9b3b9b",
    lineHeight: 1.4,
    marginBottom: 10,
  },
  devDesc: {
    fontSize: "0.74rem",
    color: "#666666",
    lineHeight: 1.6,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginBottom: 12,
  },
  devSkillsRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "6px",
    marginBottom: 14,
    minHeight: 24,
  },
  devSkillChip: {
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#4c004c",
    background: "#f6eef6",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 20,
    padding: "3px 10px",
    whiteSpace: "nowrap",
  },
  devArrowRow: {
    marginTop: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.4px",
    transition: "color 0.3s ease",
    "& svg": { fontSize: 14 },
  },

  // ── Quote ──
  quoteSection: {
    position: "relative",
    marginTop: 46,
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: 18,
    padding: "34px 30px 32px",
    textAlign: "center",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 300,
      height: 300,
      borderRadius: "50%",
      top: -150,
      left: -90,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.28), transparent 70%)",
      pointerEvents: "none",
    },
  },
  quoteMark: {
    position: "relative",
    zIndex: 1,
    color: "rgba(255,255,255,0.35)",
    fontSize: 34,
    marginBottom: 2,
  },
  quoteText: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.9rem",
    color: "#ffffff",
    lineHeight: 1.7,
    fontStyle: "italic",
    maxWidth: 720,
    margin: "0 auto 14px",
  },
  quoteAuthor: {
    position: "relative",
    zIndex: 1,
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },

  // ── CTA band ──
  ctaBand: {
    position: "relative",
    overflow: "hidden",
    marginTop: 24,
    borderRadius: 18,
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    padding: "38px 30px",
    textAlign: "center",
    boxShadow: "0 16px 44px rgba(76,0,76,0.25)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 320,
      height: 320,
      borderRadius: "50%",
      top: -160,
      right: -80,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.3), transparent 70%)",
      pointerEvents: "none",
    },
  },
  ctaBandTitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 8,
  },
  ctaBandSub: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.8)",
    maxWidth: 460,
    margin: "0 auto 22px",
    lineHeight: 1.6,
  },
  ctaBandActions: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  ctaBandBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "#ffffff",
    color: "#4c004c",
    fontSize: "0.82rem",
    fontWeight: 600,
    borderRadius: 30,
    padding: "11px 26px",
    textDecoration: "none",
    boxShadow: "0 10px 26px rgba(0,0,0,0.25)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 34px rgba(0,0,0,0.35)",
    },
    "& svg": { fontSize: 16 },
  },
  ctaBandGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    color: "#ffffff",
    fontSize: "0.82rem",
    fontWeight: 500,
    borderRadius: 30,
    padding: "11px 24px",
    border: "1px solid rgba(255,255,255,0.4)",
    textDecoration: "none",
    transition: "all 0.3s ease",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#ffffff",
      textDecoration: "none",
    },
    "&:hover": {
      background: "rgba(255,255,255,0.1)",
      borderColor: "rgba(255,255,255,0.7)",
    },
  },
}));

export const OurTeam = () => {
  const classes = useStyles();
  const [team1, setTeam1] = useState([]);
  const [team2, setTeam2] = useState([]);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [isloading, setIsloading] = useState(true);

  useEffect(() => {
    getMetaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMetaData = async () => {
    const formData = new FormData();
    formData.append("page", 8);
    await Axios.post("/api/getmetadata", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        setMetaTitle(res.data.data.meta_title);
        setMetaKeywords(res.data.data.meta_keywords);
        setMetaDesc(res.data.data.meta_desc);
      }
    });
    await Axios.post("/api/get_all_team", {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        setTeam1(res.data.team1);
        setTeam2(res.data.team2);
        setIsloading(false);
      }
    });
  };

  return (
    <div>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords}></meta>
      </Helmet>

      {/* Hero */}
      <Box className={classes.heroSection}>
        <HeroParticles id="ourteam-hero-particles" />
        <Box className={`${classes.heroOrb} ${classes.heroOrbOne}`} />
        <Box className={`${classes.heroOrb} ${classes.heroOrbTwo}`} />
        <Box className={classes.heroContent}>
          <span className={classes.heroEyebrow}>
            The people behind the products
          </span>
          <Typography className={classes.heroTitle}>Our Team</Typography>
          <Box className={classes.heroDivider} />
          <Typography className={classes.heroSubtitle}>
            A small team that designs, builds and ships everything you see
            here.
          </Typography>
        </Box>
      </Box>

      {isloading ? (
        <Loading />
      ) : (
        <Box className={classes.pageWrapper}>
          {/* Floating stats bar */}
          <Box className={classes.statsBar}>
            <Box className={classes.statItem}>
              <Box className={classes.statIcon}>
                <EmojiEventsOutlinedIcon />
              </Box>
              <span className={classes.statLabel}>
                {team1.length} Leaders
              </span>
            </Box>
            <Box className={classes.statItem}>
              <Box className={classes.statIcon}>
                <PeopleAltOutlinedIcon />
              </Box>
              <span className={classes.statLabel}>
                {team2.length} Core Developers
              </span>
            </Box>
            <Box className={classes.statItem}>
              <Box className={classes.statIcon}>
                <FlagOutlinedIcon />
              </Box>
              <span className={classes.statLabel}>Building since 2018</span>
            </Box>
          </Box>

          <Container maxWidth="lg">
            {/* Leadership */}
            <Reveal>
              <Box className={classes.sectionHead}>
                <Typography
                  className={`${classes.sectionKicker} ${classes.sectionKickerGold}`}
                >
                  Leadership Team
                </Typography>
                <Typography className={classes.sectionTitle}>
                  The people steering Arawinz
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Decades of experience, still writing specs and reviewing
                  builds every week.
                </Typography>
              </Box>
            </Reveal>

            <Box className={classes.leaderGrid}>
              {team1.map((member, index) => (
                <Reveal delay={(index % 2) * 110} key={member.id}>
                  <Link to={`/team/${member.id}`} className={classes.leaderCard}>
                    <Box className={classes.leaderPortraitWrap}>
                      <img
                        src={"/static/team/" + member.image}
                        alt={member.name}
                        className={classes.leaderPortrait}
                      />
                      <Box className={classes.leaderCrest}>
                        <StarIcon />
                      </Box>
                    </Box>
                    <Box className={classes.leaderBody}>
                      <Typography className={classes.leaderKicker}>
                        Leadership
                      </Typography>
                      <Typography className={classes.leaderName}>
                        {member.name}
                      </Typography>
                      <Typography className={classes.leaderDesig}>
                        {member.designation}
                      </Typography>
                      <Typography className={classes.leaderDesc}>
                        {member.description}
                      </Typography>
                      <Box className={classes.leaderFoot}>
                        <span className={classes.leaderArrow}>
                          <ArrowForwardIcon />
                        </span>
                        <span className={classes.leaderFootLabel}>
                          View profile
                        </span>
                      </Box>
                    </Box>
                  </Link>
                </Reveal>
              ))}
            </Box>

            {/* Core developers */}
            <Reveal>
              <Box className={classes.sectionHead}>
                <Typography className={classes.sectionKicker}>
                  Core Developers
                </Typography>
                <Typography className={classes.sectionTitle}>
                  The people building the products
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Engineers who own their work end to end, from first commit to
                  production.
                </Typography>
              </Box>
            </Reveal>

            <Box className={classes.devGrid}>
              {team2.map((member, index) => {
                const skills = cleanSkills(member.skill).slice(0, 3);
                return (
                  <Reveal delay={(index % 3) * 100} key={member.id}>
                    <Link to={`/team/${member.id}`} className={classes.devCard}>
                      <Box className={classes.devPortraitWrap}>
                        <img
                          src={"/static/team/" + member.image}
                          alt={member.name}
                          className={classes.devPortrait}
                        />
                        <Box className={classes.devCrest}>
                          <CodeIcon />
                        </Box>
                      </Box>
                      <Typography className={classes.devName}>
                        {member.name}
                      </Typography>
                      <Typography className={classes.devDesig}>
                        {member.designation}
                      </Typography>
                      <Typography className={classes.devDesc}>
                        {member.description}
                      </Typography>
                      {skills.length > 0 && (
                        <Box className={classes.devSkillsRow}>
                          {skills.map((s, i) => (
                            <span key={i} className={classes.devSkillChip}>
                              {s}
                            </span>
                          ))}
                        </Box>
                      )}
                      <span className={classes.devArrowRow}>
                        View profile <ArrowForwardIcon />
                      </span>
                    </Link>
                  </Reveal>
                );
              })}
            </Box>

            {/* Quote */}
            <Reveal>
              <Box className={classes.quoteSection}>
                <FormatQuoteIcon className={classes.quoteMark} />
                <TypingQuote
                  text={`"At Arawinz, creativity and commitment sit at the heart of everything we build, and it shows in the results we deliver. We hold ourselves to a high standard, work hard to understand what each client actually needs, and build smarter, more connected products together."`}
                  className={classes.quoteText}
                />
                <Typography className={classes.quoteAuthor}>
                  - Dr. P. Srinivas Rao, CEO
                </Typography>
              </Box>
            </Reveal>

            {/* CTA */}
            <Reveal>
              <Box className={classes.ctaBand}>
                <Typography className={classes.ctaBandTitle}>
                  Like how this team works?
                </Typography>
                <Typography className={classes.ctaBandSub}>
                  We are always looking for people who build things properly,
                  and for businesses that want them to.
                </Typography>
                <Box className={classes.ctaBandActions}>
                  <Link to="/careers" className={classes.ctaBandBtn}>
                    See Open Roles <ArrowForwardIcon />
                  </Link>
                  <Link to="/get-in-touch" className={classes.ctaBandGhost}>
                    Get in Touch
                  </Link>
                </Box>
              </Box>
            </Reveal>
          </Container>
        </Box>
      )}
    </div>
  );
};
